import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { divideNumbersAsDecimals, FormTextareaField, lt, multiplyNumbersAsDecimals } from '@mews/framework';
import { Button, Grid, Icon, IconName, Stack } from '@optimus-web/core';
import { isDefined } from '@mews/std';
import { RefundMode } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { PaymentAmountInputField } from '../../../common/PaymentAmountInputField';
import { roundToTwoDecimals } from '../utils';
import { PaymentRefundWithFXFormValues } from '../schema';
import { PaymentRefundFormField, PaymentRefundWithFXFormField } from '../types';
import { PaymentTerminalSelectField } from '../../ProcessPayment/MewsTerminalPayment/PaymentTerminalSelectField';

export const PaymentRefundWithFXFormFields = ({
    chargedCurrencyCode,
    propertyCurrencyCode,
    refundConversionMultiplier,
}: {
    chargedCurrencyCode: string;
    propertyCurrencyCode: string;
    refundConversionMultiplier: number;
}) => {
    const [activeField, setActiveField] = useState<
        PaymentRefundWithFXFormField.ChargedCurrencyAmount | PaymentRefundWithFXFormField.PropertyCurrencyAmount
    >(PaymentRefundWithFXFormField.ChargedCurrencyAmount);
    const { watch, getValues, setValue, trigger } = useFormContext<PaymentRefundWithFXFormValues>();

    const refundMode = getValues(PaymentRefundFormField.RefundMode);
    const chargedCurrencyAmount = watch(PaymentRefundWithFXFormField.ChargedCurrencyAmount);
    const propertyCurrencyAmount = watch(PaymentRefundWithFXFormField.PropertyCurrencyAmount);

    useEffect(() => {
        if (isDefined(chargedCurrencyAmount) && activeField === PaymentRefundWithFXFormField.ChargedCurrencyAmount) {
            const calculatedPropertyCurrencyAmount = multiplyNumbersAsDecimals(
                chargedCurrencyAmount || 0,
                refundConversionMultiplier
            );
            setValue(
                PaymentRefundWithFXFormField.PropertyCurrencyAmount,
                roundToTwoDecimals(calculatedPropertyCurrencyAmount)
            );
            void trigger([
                PaymentRefundWithFXFormField.PropertyCurrencyAmount,
                PaymentRefundWithFXFormField.ChargedCurrencyAmount,
            ]);
        }
    }, [chargedCurrencyAmount, activeField, refundConversionMultiplier, setValue, trigger]);

    useEffect(() => {
        if (isDefined(propertyCurrencyAmount) && activeField === PaymentRefundWithFXFormField.PropertyCurrencyAmount) {
            const calculatedChargedCurrencyAmount = divideNumbersAsDecimals(
                propertyCurrencyAmount || 0,
                refundConversionMultiplier
            );
            setValue(
                PaymentRefundWithFXFormField.ChargedCurrencyAmount,
                roundToTwoDecimals(calculatedChargedCurrencyAmount)
            );
            void trigger([
                PaymentRefundWithFXFormField.PropertyCurrencyAmount,
                PaymentRefundWithFXFormField.ChargedCurrencyAmount,
            ]);
        }
    }, [propertyCurrencyAmount, activeField, refundConversionMultiplier, setValue, trigger]);

    return (
        <Stack vertical spacing="300">
            <Grid templateColumns="4fr 35px 4fr">
                <PaymentAmountInputField
                    name={PaymentRefundWithFXFormField.ChargedCurrencyAmount}
                    currencyCode={chargedCurrencyCode}
                    label={lt('PaymentRefundAmountInChargedCurrency')}
                    onFocus={() => setActiveField(PaymentRefundWithFXFormField.ChargedCurrencyAmount)}
                />
                <Stack vertical spacing="300">
                    <div />
                    <Button variant="ghost" disabled type="submit">
                        <Icon value={IconName.ChannelManager} variant="inherit" />
                    </Button>
                </Stack>
                <PaymentAmountInputField
                    name={PaymentRefundWithFXFormField.PropertyCurrencyAmount}
                    currencyCode={propertyCurrencyCode}
                    label={lt('PaymentRefundAmountInPropertyCurrency')}
                    onFocus={() => setActiveField(PaymentRefundWithFXFormField.PropertyCurrencyAmount)}
                />
            </Grid>
            {refundMode === RefundMode.InPerson ? (
                <PaymentTerminalSelectField
                    helperTextKey={TextKey.InPersonPaymentRefundFormInfo}
                    terminalFilter={(t) => t.isStripeTerminal}
                />
            ) : null}
            <FormTextareaField name={PaymentRefundWithFXFormField.Notes} label={lt('Notes')} required rows={3} />
        </Stack>
    );
};
